import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ShieldCheck, Wrench } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import AdminCard from "./AdminCard";

const ROLE_PRESETS = {
    moderator: { label: "Moderator", desc: "Dostęp do CMS. Zarządzanie użytkownikami tylko po nadaniu uprawnienia." },
    worker: { label: "Wykonawca", desc: "Widzi tylko zgłoszenia ze swoich kategorii. Może zmieniać status i dodawać notatki." },
};

export default function UzytkownicyAdmin({ currentUser }) {
    const [users, setUsers] = useState([]);
    const [form, setForm] = useState({ email: "", password: "", name: "", roleType: "moderator", assigned_report_categories: [] });
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(false);
    const [permMeta, setPermMeta] = useState({ keys: [], labels: {}, grantable: [] });
    const [categories, setCategories] = useState([]);

    const isAdmin = currentUser?.role === "admin";
    const canManage = isAdmin || !!currentUser?.permissions?.manage_users;
    const holdsOnlyMine = (u) => Object.entries(u.permissions || {}).every(([k, v]) => !v || !!currentUser?.permissions?.[k]);
    const canEditUser = (u) => !u.is_main && (isAdmin || (u.role !== "admin" && u.id !== currentUser?._id && holdsOnlyMine(u)));
    const canGrant = (k) => isAdmin || (permMeta.grantable || []).includes(k);

    const load = () => api.get("/users").then((r) => setUsers(r.data || []));
    useEffect(() => {
        load();
        if (canManage) {
            api.get("/users/permissions/available").then((r) => setPermMeta(r.data || { keys: [], labels: {}, grantable: [] })).catch(() => {});
            api.get("/report-categories").then((r) => setCategories(r.data || [])).catch(() => {});
        }
    }, [canManage]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                email: form.email,
                password: form.password,
                name: form.name,
            };
            if (form.roleType === "worker") {
                if (!form.assigned_report_categories.length) {
                    toast.error("Wybierz przynajmniej jedną kategorię dla wykonawcy.");
                    setLoading(false);
                    return;
                }
                payload.assigned_report_categories = form.assigned_report_categories;
            }
            await api.post("/users", payload);
            toast.success(form.roleType === "worker" ? "Wykonawca dodany." : "Moderator dodany.");
            setForm({ email: "", password: "", name: "", roleType: "moderator", assigned_report_categories: [] });
            await load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setLoading(false); }
    };

    const saveEdit = async () => {
        if (!editing) return;
        try {
            const payload = { name: editing.name };
            if (editing.new_password) payload.password = editing.new_password;
            if (editing.permissions) payload.permissions = editing.permissions;
            if (editing.assigned_report_categories) payload.assigned_report_categories = editing.assigned_report_categories;
            await api.put(`/users/${editing.id}`, payload);
            toast.success("Zaktualizowano.");
            setEditing(null);
            await load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const del = async (id, isMain) => {
        if (isMain) { toast.error("Nie można usunąć głównego administratora."); return; }
        if (!window.confirm("Usunąć tego użytkownika?")) return;
        try {
            await api.delete(`/users/${id}`);
            toast.success("Usunięto użytkownika.");
            await load();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    const toggleFormCat = (key) => {
        setForm((f) => ({
            ...f,
            assigned_report_categories: f.assigned_report_categories.includes(key)
                ? f.assigned_report_categories.filter((k) => k !== key)
                : [...f.assigned_report_categories, key],
        }));
    };

    const toggleEditCat = (key) => {
        setEditing((e) => ({
            ...e,
            assigned_report_categories: (e.assigned_report_categories || []).includes(key)
                ? (e.assigned_report_categories || []).filter((k) => k !== key)
                : [...(e.assigned_report_categories || []), key],
        }));
    };

    return (
        <div className="grid lg:grid-cols-2 gap-6">
            <AdminCard>
                <h3 className="font-serif text-xl text-[#092E22] mb-1">Dodaj użytkownika</h3>
                <p className="text-sm text-gray-500 mb-5">Moderator ma pełen dostęp do CMS. Wykonawca widzi i obsługuje tylko zgłoszenia ze swoich kategorii.</p>
                {!canManage ? (
                    <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-3">
                        Brak uprawnienia „Zarządzanie użytkownikami”.
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-4" data-testid="admin-create-user-form">
                        <div className="space-y-2">
                            <Label>Typ konta *</Label>
                            <div className="grid grid-cols-2 gap-2">
                                {Object.entries(ROLE_PRESETS).map(([k, v]) => (
                                    <button
                                        type="button"
                                        key={k}
                                        onClick={() => setForm({ ...form, roleType: k })}
                                        data-testid={`admin-create-user-role-${k}`}
                                        className={`text-left p-3 rounded-xl border transition-colors ${form.roleType === k ? "border-[#0D4F3C] bg-[#0D4F3C]/5" : "border-gray-200 hover:border-gray-300"}`}
                                    >
                                        <div className="flex items-center gap-2 text-sm font-semibold text-[#092E22]">
                                            {k === "worker" ? <Wrench size={14} /> : <ShieldCheck size={14} />}
                                            {v.label}
                                        </div>
                                        <p className="text-[11px] text-gray-500 mt-1 leading-snug">{v.desc}</p>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Imię i nazwisko (opcjonalnie)</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Anna Nowak" data-testid="admin-create-user-name" />
                        </div>
                        <div className="space-y-2">
                            <Label>E-mail *</Label>
                            <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="konserwator@firma.pl" data-testid="admin-create-user-email" />
                        </div>
                        <div className="space-y-2">
                            <Label>Hasło tymczasowe * (min. 6 znaków)</Label>
                            <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-create-user-password" />
                            <p className="text-[11px] text-gray-500">Po pierwszym logowaniu użytkownik może je zmienić w zakładce „Moje konto".</p>
                        </div>
                        {form.roleType === "worker" && (
                            <div className="space-y-2">
                                <Label>Kategorie zgłoszeń przypisane do wykonawcy *</Label>
                                <div className="grid grid-cols-1 gap-1.5 bg-[#FAF8F3] rounded-xl p-3 max-h-56 overflow-y-auto" data-testid="admin-create-user-categories">
                                    {categories.length === 0 && <p className="text-xs text-gray-400 italic">Brak kategorii — dodaj najpierw w „Kategorie &amp; e-maile".</p>}
                                    {categories.map((c) => (
                                        <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer py-1" data-testid={`admin-create-user-cat-${c.key}`}>
                                            <input
                                                type="checkbox"
                                                className="w-4 h-4 accent-[#0D4F3C]"
                                                checked={form.assigned_report_categories.includes(c.key)}
                                                onChange={() => toggleFormCat(c.key)}
                                            />
                                            <span className="text-[#092E22]">{c.label}</span>
                                            <span className="text-[10px] text-gray-400 font-mono">{c.key}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                        <Button type="submit" disabled={loading} data-testid="admin-create-user-submit" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                            <Plus size={14} className="mr-2" /> {loading ? "Dodawanie..." : `Dodaj ${form.roleType === "worker" ? "wykonawcę" : "moderatora"}`}
                        </Button>
                    </form>
                )}
            </AdminCard>

            <AdminCard>
                <h3 className="font-serif text-xl text-[#092E22] mb-5">Lista użytkowników ({users.length})</h3>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {users.map((u) => {
                        const perms = u.permissions || {};
                        const isWorker = !perms.manage_reports && !!perms.handle_assigned_reports;
                        return (
                        <div key={u.id} className="flex items-center justify-between p-4 rounded-2xl bg-[#FAF8F3] border border-gray-100" data-testid={`admin-user-${u.id}`}>
                            <div>
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <p className="font-semibold text-[#092E22] text-sm">{u.name || u.email}</p>
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-semibold ${u.role === "admin" ? "bg-[#D4A537]/20 text-[#B88A26]" : isWorker ? "bg-indigo-100 text-indigo-800" : "bg-blue-100 text-blue-800"}`}>
                                        {u.role === "admin" ? "admin" : isWorker ? "wykonawca" : "moderator"}
                                    </span>
                                    {u.is_main && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-semibold bg-emerald-100 text-emerald-800">
                                            <ShieldCheck size={9} /> Główny
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs text-gray-500">{u.email}</p>
                                {isWorker && (u.assigned_report_categories?.length || 0) > 0 && (
                                    <div className="mt-1.5 flex flex-wrap gap-1">
                                        {u.assigned_report_categories.map((k) => (
                                            <span key={k} className="px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[10px] font-mono">{k}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {canManage && canEditUser(u) && (
                                <div className="flex items-center gap-1">
                                    <button onClick={() => setEditing({
                                        id: u.id,
                                        name: u.name || "",
                                        new_password: "",
                                        permissions: u.permissions || Object.fromEntries(permMeta.keys.map(k => [k, true])),
                                        assigned_report_categories: u.assigned_report_categories || [],
                                    })} data-testid={`admin-user-edit-${u.id}`} className="text-[#0D4F3C] hover:text-[#092E22] p-1.5 rounded-lg hover:bg-white" title="Edytuj">
                                        <Pencil size={16} />
                                    </button>
                                    {u.id !== currentUser?._id && (
                                        <button onClick={() => del(u.id, u.is_main)} data-testid={`admin-user-delete-${u.id}`} className="text-red-500 hover:text-red-700 p-1.5 rounded-lg hover:bg-white" title="Usuń">
                                            <Trash2 size={16} />
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        );
                    })}
                </div>
            </AdminCard>

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
                    <div className="bg-white rounded-3xl shadow-2xl max-w-xl w-full p-7 my-8" onClick={(e) => e.stopPropagation()} data-testid="admin-user-edit-modal">
                        <h3 className="font-serif text-2xl text-[#092E22] mb-1">Edycja użytkownika</h3>
                        <p className="text-sm text-gray-500 mb-5">Zmień nazwę, hasło, uprawnienia i przypisane kategorie.</p>
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label>Imię i nazwisko</Label>
                                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="admin-user-edit-name" />
                            </div>
                            <div className="space-y-2">
                                <Label>Nowe hasło (zostaw puste, żeby nie zmieniać)</Label>
                                <Input type="password" value={editing.new_password} onChange={(e) => setEditing({ ...editing, new_password: e.target.value })} data-testid="admin-user-edit-password" placeholder="min. 6 znaków" />
                            </div>
                            {permMeta.keys.length > 0 && editing.permissions && (
                                <div className="space-y-2">
                                    <Label>Uprawnienia w panelu administratora</Label>
                                    <p className="text-xs text-gray-500">Włącz/wyłącz dostęp do poszczególnych zakładek panelu. Główny administrator zawsze ma pełen dostęp.</p>
                                    {!isAdmin && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2" data-testid="admin-user-edit-grant-note">Możesz nadawać tylko uprawnienia, które sam posiadasz.</p>}
                                    <div className="grid grid-cols-1 gap-2 mt-2 bg-[#FAF8F3] rounded-xl p-4" data-testid="admin-user-edit-permissions">
                                        {permMeta.keys.map((k) => (
                                            <label key={k} className="flex items-center justify-between gap-3 py-1 cursor-pointer" data-testid={`admin-user-edit-perm-${k}`}>
                                                <span className="text-sm text-[#092E22]">{permMeta.labels[k] || k}</span>
                                                <Switch
                                                    data-testid={`admin-user-edit-perm-switch-${k}`}
                                                    disabled={!canGrant(k) && !editing.permissions[k]}
                                                    checked={!!editing.permissions[k]}
                                                    onCheckedChange={(v) => setEditing({ ...editing, permissions: { ...editing.permissions, [k]: v } })}
                                                />
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {editing.permissions?.handle_assigned_reports && (
                                <div className="space-y-2">
                                    <Label>Kategorie zgłoszeń (dla wykonawcy)</Label>
                                    <p className="text-xs text-gray-500">Wykonawca zobaczy tylko zgłoszenia z zaznaczonych kategorii.</p>
                                    <div className="grid grid-cols-1 gap-1.5 bg-[#FAF8F3] rounded-xl p-3 max-h-56 overflow-y-auto" data-testid="admin-user-edit-categories">
                                        {categories.length === 0 && <p className="text-xs text-gray-400 italic">Brak kategorii.</p>}
                                        {categories.map((c) => (
                                            <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer py-1" data-testid={`admin-user-edit-cat-${c.key}`}>
                                                <input
                                                    type="checkbox"
                                                    className="w-4 h-4 accent-[#0D4F3C]"
                                                    checked={(editing.assigned_report_categories || []).includes(c.key)}
                                                    onChange={() => toggleEditCat(c.key)}
                                                />
                                                <span className="text-[#092E22]">{c.label}</span>
                                                <span className="text-[10px] text-gray-400 font-mono">{c.key}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="flex gap-3 justify-end pt-2">
                                <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full" data-testid="admin-user-edit-cancel">Anuluj</Button>
                                <Button onClick={saveEdit} className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full" data-testid="admin-user-edit-save">Zapisz</Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
